import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { Megaphone, Send } from "lucide-react";
import { toast } from "sonner";

const SEGMENTS = [
    { key: "all", label: "Everyone" },
    { key: "premium", label: "Premium" },
    { key: "free", label: "Free users" },
    { key: "verified", label: "Verified" },
];

export default function AdminBroadcast() {
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [url, setUrl] = useState("");
    const [segment, setSegment] = useState("all");
    const [counts, setCounts] = useState({});
    const [sending, setSending] = useState(false);

    useEffect(() => {
        adminApi.get("/admin/broadcast/segments").then(({ data }) => setCounts(data)).catch(() => {});
    }, []);

    const send = async () => {
        if (!title.trim() || !body.trim()) { toast.error("Title and message required"); return; }
        if (!window.confirm(`Send to ${counts[segment] ?? "?"} ${segment} users?`)) return;
        setSending(true);
        try {
            const { data } = await adminApi.post("/admin/broadcast", { title, body, url: url || null, segment });
            toast.success(`Sent to ${data.sent} users 📣`);
            setTitle(""); setBody(""); setUrl("");
        } catch (e) {
            toast.error("Broadcast failed");
        } finally { setSending(false); }
    };

    return (
        <div data-testid="admin-broadcast-page" className="max-w-2xl">
            <h1 className="font-display text-3xl font-black flex items-center gap-2">
                <Megaphone className="h-7 w-7 text-[#E85D5D]" /> Broadcast
            </h1>
            <p className="text-sm text-white/50 mt-1">Push an announcement to users — appears in their notifications + as a browser push.</p>

            <div className="mt-6 space-y-4">
                <div>
                    <label className="text-[10px] font-black uppercase tracking-widest text-white/50">Audience</label>
                    <div className="mt-2 flex flex-wrap gap-2">
                        {SEGMENTS.map((sg) => (
                            <button
                                key={sg.key}
                                onClick={() => setSegment(sg.key)}
                                data-testid={`broadcast-segment-${sg.key}`}
                                className={`px-4 py-2 rounded-xl text-sm font-bold border transition ${
                                    segment === sg.key ? "bg-[#E85D5D] border-[#E85D5D] text-white" : "border-white/10 bg-white/5 text-white/70 hover:text-white"
                                }`}
                            >
                                {sg.label}
                                {counts[sg.key] != null && <span className="ml-1.5 opacity-70">({counts[sg.key]})</span>}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="text-[10px] font-black uppercase tracking-widest text-white/50">Title</label>
                    <input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        maxLength={80}
                        data-testid="broadcast-title"
                        placeholder="New feature just dropped 🎉"
                        className="mt-1 h-11 w-full rounded-xl bg-white/5 border border-white/10 px-3 text-sm outline-none focus:border-[#E85D5D]"
                    />
                </div>
                <div>
                    <label className="text-[10px] font-black uppercase tracking-widest text-white/50">Message</label>
                    <textarea
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        maxLength={280}
                        rows={3}
                        data-testid="broadcast-body"
                        placeholder="Tell your users what's up…"
                        className="mt-1 w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#E85D5D] resize-none"
                    />
                    <div className="text-[10px] text-white/40 text-right">{body.length}/280</div>
                </div>
                <div>
                    <label className="text-[10px] font-black uppercase tracking-widest text-white/50">Link (optional)</label>
                    <input
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        data-testid="broadcast-url"
                        placeholder="/app/match"
                        className="mt-1 h-11 w-full rounded-xl bg-white/5 border border-white/10 px-3 text-sm outline-none focus:border-[#E85D5D]"
                    />
                </div>

                <button
                    onClick={send}
                    disabled={sending}
                    data-testid="broadcast-send-btn"
                    className="h-12 rounded-2xl bg-[#E85D5D] hover:bg-[#E85D5D]/90 px-6 font-black flex items-center gap-2 disabled:opacity-50"
                >
                    <Send className="h-4 w-4" /> {sending ? "Sending…" : `Send to ${counts[segment] ?? ""} ${segment}`}
                </button>
            </div>
        </div>
    );
}
